import React, { useState, useEffect, useContext, useLayoutEffect } from "react";
import "../style/odds.css";
import axios from "axios";
import Spinner from "./Spinner";
import { AuthContext } from "../context/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { io } from "socket.io-client";
import ReplayIcon from "@mui/icons-material/Replay";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import api_url from "../constants";

const ENDPOINT =
  process.env.NODE_ENV === "development"
    ? "http://localhost:9003"
    : "https://api.bsf5050.com";

const months = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function AllOdds() {
  const [authContext] = useContext(AuthContext);
  const config = {
    headers: { Authorization: `Bearer ${authContext.token}` },
  };
  const [isRefreshing, setRefreshing] = useState(false);
  const [password, setPassword] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [matchOdds, setMatchOdds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchList, setMatchList] = useState([]);
  const [unresolvedMatches, setUnresolvedMatches] = useState([]);
  const [valueList, setValueList] = useState([]);
  const [resultLoading, setResultLoading] = useState("");
  const [matchLoading, setMatchLoading] = useState("");
  
  // --- Locking States ---
  const [showLockConfirmModal, setShowLockConfirmModal] = useState(false);
  const [pendingLock, setPendingLock] = useState(null); // { action: 'lock' | 'declare', type: 'session' | 'match', fancyName, resultValue, matchId, resultText? }
  const [lockedBets, setLockedBets] = useState({}); // Stores the initially locked value for sessions { fancyName: value }
  const [lockedMatchBets, setLockedMatchBets] = useState({}); // Stores the initially locked value for matches { matchId: winnerSid }

  // --- Filter States ---
  const [filterMatchId, setFilterMatchId] = useState("");
  const [filterType, setFilterType] = useState("All"); // All, Match Bets, Session Bets

  // --- Toggle States (Main Sections) ---
  const [showMatchSection, setShowMatchSection] = useState(false);
  const [showSessionSection, setShowSessionSection] = useState(true);

  // --- Toggle State (Individual Session Matches) ---
  const [visibleSessions, setVisibleSessions] = useState({});

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleSession = (matchId) => {
    setVisibleSessions((prev) => ({
      ...prev,
      [matchId]: !prev[matchId],
    }));
  };

  // --- Match Winner Lock & Declare Logic ---
  const handleMatchLockClick = (matchId, eventName) => {
    const selectEl = document.getElementById(matchId);
    const winnerSid = selectEl?.value;
    const resultText = selectEl?.options[selectEl.selectedIndex]?.text; // To display human readable name in modal

    if (!winnerSid) {
      toast.error("Please select a winner result");
      return;
    }

    const isLocked = lockedMatchBets[matchId] !== undefined;

    if (isLocked) {
      // DECLARE PHASE
      if (winnerSid !== lockedMatchBets[matchId]) {
        toast.error("Values do not match! Resetting to lock phase.");
        if (selectEl) selectEl.value = ""; // Reset to default
        setLockedMatchBets(prev => {
          const updated = { ...prev };
          delete updated[matchId];
          return updated;
        });
        return;
      }

      setPendingLock({ 
        action: 'declare', type: 'match', matchId, resultValue: winnerSid, resultText, fancyName: eventName 
      });
      setShowLockConfirmModal(true);
    } else {
      // LOCK PHASE
      setPendingLock({ 
        action: 'lock', type: 'match', matchId, resultValue: winnerSid, resultText, fancyName: eventName 
      });
      setShowLockConfirmModal(true);
    }
  };

  // --- Session Bets Lock & Declare Logic ---
  const handleLockClick = (fancyName, matchId) => {
    const inputEl = document.getElementById(`result-${fancyName}`);
    const valueFromInput = inputEl?.value?.trim();
    const valFromList = valueList.find((x) => x.fancyName === fancyName);
    const value = valueFromInput ?? valFromList?.value ?? "";

    if (!value) {
      toast.error("Please enter a result");
      return;
    }

    const isLocked = lockedBets[fancyName] !== undefined;

    if (isLocked) {
      // DECLARE PHASE
      if (value !== lockedBets[fancyName]) {
        toast.error("Values do not match! Resetting to lock phase.");
        if (inputEl) inputEl.value = "";
        setValueList((prev) => prev.filter((x) => x.fancyName !== fancyName));
        setLockedBets(prev => {
          const updated = { ...prev };
          delete updated[fancyName];
          return updated;
        });
        return; 
      }
      
      setPendingLock({ action: 'declare', type: 'session', fancyName, resultValue: value, matchId });
      setShowLockConfirmModal(true);
    } else {
      // LOCK PHASE
      setPendingLock({ action: 'lock', type: 'session', fancyName, resultValue: value, matchId });
      setShowLockConfirmModal(true);
    }
  };

  // --- Unified Process Action (Fired by Confirm Modal) ---
  const processAction = async () => {
    if (!pendingLock) return;
    const { action, type, fancyName, resultValue, matchId } = pendingLock;
    
    // --- MATCH BETS PROCESSING ---
    if (type === 'match') {
      if (action === 'lock') {
        setLockedMatchBets(prev => ({ ...prev, [matchId]: resultValue }));
        toast.success(`Match result locked. Please re-select to declare.`);
        
        const selectEl = document.getElementById(matchId);
        if (selectEl) selectEl.value = ""; 
        
        setShowLockConfirmModal(false);
        setPendingLock(null);
      } else if (action === 'declare') {
        setMatchLoading(matchId);
        setShowLockConfirmModal(false);
        setPendingLock(null);

        try {
          const response = await axios.post(
            `${api_url}/bet/settleMatchBet`,
            { matchId, winnerSid: resultValue },
            config
          );
          toast.success(response.data.msg);
          
          const selectEl = document.getElementById(matchId);
          if (selectEl) selectEl.value = "";
          
          setLockedMatchBets(prev => {
            const updated = { ...prev };
            delete updated[matchId];
            return updated;
          });
        } catch (error) {
          toast.error(error.response?.data?.msg || "Failed to declare match result. Please try again.");
          console.error(error);
        } finally {
          setMatchLoading("");
        }
      }
      return; // Stop execution here for match bets
    }

    // --- SESSION BETS PROCESSING ---
    if (type === 'session') {
      if (action === 'lock') {
        setLockedBets(prev => ({ ...prev, [fancyName]: resultValue }));
        toast.success(`Result locked. Please re-enter to declare.`);
        
        const inputEl = document.getElementById(`result-${fancyName}`);
        if (inputEl) inputEl.value = "";
        
        setValueList((prev) => prev.filter((x) => x.fancyName !== fancyName));
        setShowLockConfirmModal(false);
        setPendingLock(null);
      } else if (action === 'declare') {
        setResultLoading(fancyName);
        setShowLockConfirmModal(false);
        setPendingLock(null);

        try {
          const response = await axios.post(
            `${api_url}/bet/settleBet`,
            { fancyName, value: resultValue, matchId },
            config
          );
          toast.success(response.data.msg);
          
          const inputEl = document.getElementById(`result-${fancyName}`);
          if (inputEl) inputEl.value = "";
          
          setValueList((prev) => prev.filter((x) => x.fancyName !== fancyName));
          
          setLockedBets(prev => {
            const updated = { ...prev };
            delete updated[fancyName];
            return updated;
          });
        } catch (error) {
          toast.error(error.response?.data?.msg || "Failed to declare result. Please try again.");
          console.error(error);
        } finally {
          setResultLoading("");
        }
      }
    }
  };

  const cancelLock = () => {
    setShowLockConfirmModal(false);
    setPendingLock(null);
  };

  const resultValue = (value, fancyName, matchId) => {
    // Extra safety: Don't update state if value is negative
    if (Number(value) < 0) return; 

    const data = { value, fancyName, matchId };
    setValueList([...valueList.filter((x) => x.fancyName !== fancyName), data]);
  };

  const getSessionOdds = async (socket) => {
    try {
      socket.on("allBets", (data) => {
        if (data) {
          setMatchOdds(data);
        }
      });
    } catch (error) {
      console.log(error);
    }
    socket.on("error", (message) => {
      setMatchOdds([]);
    });
    setLoading(false);
  };

  const getUnsettledMatch = async (socket) => {
    try {
      socket.on("unsettledMatches", (data) => {
        if (data) {
          setUnresolvedMatches(data);
        }
      });
    } catch (error) {
      console.log(error);
    }
    socket.on("error", (message) => {
      console.log(message);
      setMatchOdds({});
    });
    socket.emit("getUnsettledMatch");
    setLoading(false);
  };

  async function restartServer() {
    try {
      setPassword("");
      setShowModal(false);
      const response = await axios.post(
        `${api_url}/api/restartServer`,
        { password },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authContext.token}`,
          },
        }
      );
      if (response.data.msg) {
        toast.error(response.data.msg);
        setShowModal(false);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function getMatchData() {
    try {
      const response = await axios.get(`${api_url}/sports/getMatchList`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authContext.token}`,
        },
      });
      setMatchList(response.data);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    getMatchData();
  }, []);

  useEffect(() => {
    const socket = io(ENDPOINT, {
      path: "/socket/bets",
      transports: ["websocket"],
    });
    getSessionOdds(socket);
    getUnsettledMatch(socket);
    socket.emit("getAllBets");
    socket.emit("getUnsettledMatch");

    const interval = setInterval(() => {
      socket.emit("getAllBets");
    }, 1000);
    const interval2 = setInterval(() => {
      socket.emit("getUnsettledMatch");
    }, 3000);
    return () => {
      socket.disconnect();
      clearInterval(interval);
      clearInterval(interval2);
    };
  }, []);

  // --- Helper to Check Live Status ---
  const checkIsLive = (matchTimeStr) => {
    const matchTime = new Date(matchTimeStr);
    const currentTime = new Date();
    // 1. Must be the same day
    const isSameDay = matchTime.toDateString() === currentTime.toDateString();
    // 2. Current time must be past the match start time
    const hasStarted = currentTime >= matchTime;
    return isSameDay && hasStarted;
  };

  // --- Filter Logic ---
  const shouldShowMatchBets = filterType === "All" || filterType === "Match Bets";
  const shouldShowSessionBets = filterType === "All" || filterType === "Session Bets";

  const filteredUnresolvedMatches = unresolvedMatches.filter((match) => {
    return filterMatchId === "" || match.event_id === filterMatchId;
  });

  // Base list
  const baseMatchList = matchList.filter((match) => {
    return filterMatchId === "" || match.event_id === filterMatchId;
  });

  // --- SORTING LOGIC: Live matches to the top ---
  const sortedMatchList = [...baseMatchList].sort((a, b) => {
    const isLiveA = checkIsLive(a.time);
    const isLiveB = checkIsLive(b.time);

    // If A is live and B is not, A comes first (-1)
    if (isLiveA && !isLiveB) return -1;
    // If B is live and A is not, B comes first (1)
    if (!isLiveA && isLiveB) return 1;
    
    // If both are same status, sort by time (earliest first)
    return new Date(a.time) - new Date(b.time);
  });

  if (loading) {
    return (
      <div className="body-content no-padding">
        <Spinner color="#555" />
      </div>
    );
  }

  return (
    <>
      <style>
        {`
        .filter-container {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
          margin: 15px 10px;
        }
        .filter-select {
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 15px;
          color: #333;
          outline: none;
          cursor: pointer;
          min-width: 200px;
          transition: 0.2s ease;
        }
        .filter-select:hover, .filter-select:focus {
          border-color: #007bff;
          box-shadow: 0 0 5px rgba(0,123,255,0.3);
        }
        
        /* LIVE BLINKER ANIMATION */
        .live-indicator {
          display: inline-block;
          width: 8px;
          height: 8px;
          background-color: #00e676; /* Bright Green */
          border-radius: 50%;
          margin-right: 8px;
          box-shadow: 0 0 0 rgba(0, 230, 118, 0.4);
          animation: pulse-green 1.5s infinite;
          vertical-align: middle;
        }

        /* Prevent spin buttons on number inputs (optional, makes it look cleaner) */
        input[type=number]::-webkit-inner-spin-button, 
        input[type=number]::-webkit-outer-spin-button { 
          -webkit-appearance: none; 
          margin: 0; 
        }
        input[type=number] {
          -moz-appearance: textfield;
        }

        @keyframes pulse-green {
          0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(0, 230, 118, 0.7);
          }
          70% {
            transform: scale(1);
            box-shadow: 0 0 0 6px rgba(0, 230, 118, 0);
          }
          100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(0, 230, 118, 0);
          }
        }

        @media (max-width: 600px) {
          .filter-select {
            width: 100%;
          }
        }
        `}
      </style>

      {showLockConfirmModal && pendingLock && (
        <div className="modal-body">
          <div className="modal-content">
            <div className="modal-header">
              {pendingLock.action === 'lock' ? 'Lock' : 'Declare'}
            </div>
            <div className="modal-c" style={{ backgroundColor: "#fff", padding: "20px" }}>
              <p style={{ margin: "0 0 12px 0", fontWeight: 600, color: "#333" }}>
                {pendingLock.type === 'match' ? 'Match:' : 'Session:'} <span style={{ color: "#007bff" }}>{pendingLock.fancyName}</span>
              </p>
              <p style={{ margin: 0, fontWeight: 600, color: "#333" }}>
                Result: <span style={{ color: "#28a745", fontSize: "1.1em" }}>
                  {pendingLock.type === 'match' ? pendingLock.resultText : pendingLock.resultValue}
                </span>
              </p>
              <p style={{ margin: "16px 0 0 0", fontSize: 13, color: "#666" }}>
                {pendingLock.action === 'lock' 
                  ? 'Are you sure you want to lock this result? You will need to re-enter it to declare.' 
                  : 'Are you sure you want to declare this result and send it to the server?'}
              </p>
            </div>
            <div className="modal-btn">
              <button className={pendingLock.action === 'lock' ? "warn-btn" : "success-btn"} onClick={processAction}>
                {pendingLock.action === 'lock' ? 'Lock' : 'Declare'}
              </button>
              <button onClick={cancelLock}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showModal ? (
        <div className="modal-body">
          <div className="modal-content">
            <div className="modal-header">Enter Password</div>
            <div className="modal-c" style={{ backgroundColor: "#fff" }}>
              <div>
                <input
                  autoComplete="off"
                  type="text"
                  placeholder="Password"
                  id="amount"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-btn">
              <button
                className="warn-btn"
                onClick={() => {
                  restartServer();
                }}
              >
                Restart
              </button>
              <button onClick={() => setShowModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="body-content no-padding">
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />

        <div style={{ padding: "10px 10px 0 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{margin: 0, color: "#444"}}>Result Manager</h2>
          <button className="warn-btn" onClick={() => setShowModal(true)}>
            Restart Server
          </button>
        </div>

        {/* --- FILTERS --- */}
        <div className="filter-container">
          <select
            className="filter-select"
            value={filterMatchId}
            onChange={(e) => setFilterMatchId(e.target.value)}
          >
            <option value="">All Matches</option>
            {unresolvedMatches.map((match) => (
              <option key={match.event_id} value={match.event_id}>
                {match.event_name}
              </option>
            ))}
          </select>

          <select
            className="filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="All">All Bet Types</option>
            <option value="Match Bets">Match Bets (Winners)</option>
            <option value="Session Bets">Session Bets (Fancy)</option>
          </select>

           <span
            onClick={() => {
              setRefreshing(true);
              window.location.reload();
            }}
            className={isRefreshing ? "rotating" : ""}
            style={{ cursor: "pointer", marginLeft: "auto", color: "#555" }}
            title="Refresh Data"
          >
            <ReplayIcon style={{ fontSize: 30 }} />
          </span>
        </div>

        {/* --- SECTION 1: MATCH BETS (Unresolved Matches) --- */}
        {shouldShowMatchBets && (
          <>
            <h3
              style={{
                padding: 10,
                color: "#555",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #eee",
                cursor: "pointer",
                userSelect: "none"
              }}
              onClick={() => setShowMatchSection(!showMatchSection)}
            >
              <span>Match Winners (Unsettled)</span>
              <span>
                {showMatchSection ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </span>
            </h3>
            
            {showMatchSection && (
              <div style={{ paddingLeft: 10, paddingRight: 10 }}>
                {filteredUnresolvedMatches.length === 0 ? (
                  <div style={{padding: 15, textAlign: "center", color: "#888"}}>No Unresolved Matches Found</div>
                ) : (
                  filteredUnresolvedMatches
                    .sort((a, b) => new Date(b.time) - new Date(a.time))
                    .map((match) => {
                      const date = new Date(match.time);
                      const isMatchLocked = lockedMatchBets[match.event_id] !== undefined;

                      return (
                        <div key={match.id} className="main-btn">
                          <div className="usmb">
                            <span
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            >
                              {match.event_name}{" "}
                              <b>
                                {date.getDate() + " " + months[date.getMonth()]}:{" "}
                              </b>
                            </span>
                            <select 
                              id={match.event_id}
                              style={{
                                padding: "4px 8px",
                                borderColor: isMatchLocked ? "#f39c12" : "#ccc",
                                borderWidth: isMatchLocked ? "2px" : "1px",
                                backgroundColor: isMatchLocked ? "#fffdf5" : "#fff",
                                borderRadius: "4px",
                                outline: "none"
                              }}
                            >
                              <option value="" disabled={isMatchLocked}>
                                {isMatchLocked ? "Re-select to Declare" : "Select Winner"}
                              </option>
                              {match.markets.map((x) => {
                                return (
                                  <option value={x.runner_id} key={x.runner_id}>
                                    {x.runner_name}
                                  </option>
                                );
                              })}
                              <option value="matchTied">Match Tied</option>
                              <option value="noResult">No Result</option>
                            </select>
                          </div>
                          <button
                            type="button"
                            disabled={matchLoading === match.event_id}
                            style={{
                              backgroundColor: isMatchLocked ? "#28a745" : undefined
                            }}
                            onClick={() =>
                              matchLoading === match.event_id
                                ? console.log("Loading")
                                : handleMatchLockClick(match.event_id, match.event_name)
                            }
                          >
                            {matchLoading === match.event_id ? (
                              <Spinner color="#fff" />
                            ) : (
                              isMatchLocked ? "Declare" : "Lock"
                            )}
                          </button>
                        </div>
                      );
                    })
                )}
              </div>
            )}
          </>
        )}

        {/* --- SECTION 2: SESSION BETS (Odds Tables) --- */}
        {shouldShowSessionBets && (
          <>
             <h3
              style={{
                padding: "20px 10px 10px 10px",
                color: "#555",
                borderBottom: "1px solid #eee",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none"
              }}
              onClick={() => setShowSessionSection(!showSessionSection)}
            >
              <span>Session Bets (Fancy)</span>
              <span>
                {showSessionSection ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </span>
            </h3>

            {showSessionSection && sortedMatchList.map((match) => {
              const matchName = match.event_name;
              const data = matchOdds.filter((x) => x.match_id === match.event_id);
              const isVisible = visibleSessions[match.event_id];
              
              // Calculate Live status
              const isLive = checkIsLive(match.time);

              if (data.length === 0) return null;

              return (
                <div
                  className="pr-box"
                  key={match.event_id}
                  style={{ paddingBottom: !isVisible ? 20 : 80 }}
                >
                  <div 
                    className="prbox-header"
                    onClick={() => toggleSession(match.event_id)}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      userSelect: "none"
                    }}
                  >
                    <span>
                      {/* LIVE INDICATOR BLINKER */}
                      {isLive && <span className="live-indicator" title="Live Today"></span>}
                      {matchName}
                    </span>
                    <span style={{ display: "flex", alignItems: "center" }}>
                        {isVisible ? <KeyboardArrowUpIcon style={{ fontSize: 20 }}/> : <KeyboardArrowDownIcon style={{ fontSize: 20 }}/>}
                    </span>
                  </div>
                  
                  {isVisible && (
                  <div className="prbox-body odds-table">
                    <table>
                      <thead>
                        <tr>
                          <th>SESSION</th>
                          <th colSpan={2}>Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((odd) => {
                          const isLocked = lockedBets[odd.fancyName] !== undefined;

                          return (
                            <tr key={odd.fancyName}>
                              <td>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <span
                                    style={{
                                      width: "100%",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {odd.fancyName}
                                  </span>
                                  <span>:{odd.runs}</span>
                                </div>
                              </td>
                              <td>
                                <div className="table">
                                  <input
                                    id={`result-${odd.fancyName}`}
                                    type="number"
                                    min="0"
                                    step="1"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    placeholder={
                                      isLocked 
                                        ? "Re-enter to Declare" 
                                        : (odd.api1 && odd.api1 !== "NA" ? `${odd.api1}` : "Enter Result Here")
                                    }
                                    style={{ 
                                      padding: "8px 12px", 
                                      fontSize: "16px",
                                      borderColor: isLocked ? "#f39c12" : (odd.api1 && odd.api1 !== "NA") ? "#34495e" : "#ccc",
                                      borderWidth: isLocked || (odd.api1 && odd.api1 !== "NA") ? "2px" : "1px",
                                      backgroundColor: isLocked ? "#fffdf5" : "#fff"
                                    }}
                                    onWheel={(e) => e.target.blur()} // Prevens scroll from changing values
                                    onKeyDown={(e) => {
                                      // Prevent negative (-), decimal (.), and exponents (e/E)
                                      if (["-", "+", "e", "E", "."].includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    onChange={(e) => {
                                      // Secondary regex block to prevent pasting decimals or negatives
                                      const cleanValue = e.target.value.replace(/[^0-9]/g, "");
                                      e.target.value = cleanValue; // Updates the UI strictly
                                      
                                      resultValue(
                                        cleanValue,
                                        odd.fancyName,
                                        odd.match_id
                                      );
                                    }}
                                  />
                                </div>
                              </td>
                              <td>
                                <div
                                  className="table-flex"
                                  style={{
                                    justifyContent: "center",
                                    alignItems: "center",
                                    display: "flex",
                                  }}
                                >
                                  <button
                                    onClick={() =>
                                      resultLoading === odd.fancyName
                                        ? () => {}
                                        : resultLoading.length === 0
                                        ? handleLockClick(odd.fancyName, odd.match_id)
                                        : () => {}
                                    }
                                    disabled={
                                      (resultLoading.length > 0 &&
                                        resultLoading !== odd.fancyName) ||
                                      resultLoading !== ""
                                    }
                                    style={{
                                      border: "none",
                                      padding: "8px 12px",
                                      backgroundColor: isLocked ? "#28a745" : undefined // Green when it's ready to declare
                                    }}
                                  >
                                    {resultLoading === odd.fancyName ? (
                                      <Spinner
                                        color="#fff"
                                        style={{
                                          height: 13,
                                          width: 13,
                                          borderWidth: 2,
                                        }}
                                      />
                                    ) : (
                                      isLocked ? "Declare" : "Lock"
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div></div>
                  </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </>
  );
}

export default AllOdds;